import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '../base';

const PRESETS = [
  { icon: '💼', name: 'Work', color: '#3B82F6' },
  { icon: '🏠', name: 'Personal', color: '#10B981' },
  { icon: '📚', name: 'Studies', color: '#8B5CF6' },
  { icon: '☕', name: 'Leisure', color: '#F59E0B' },
  { icon: '⭐', name: 'Inspiration', color: '#EC4899' },
];

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (name: string, icon: string, color: string) => Promise<void>;
}

export const CreateGroupModal = ({ isOpen, onClose, onCreate }: CreateGroupModalProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [selectedColor] = useState('#6366f1');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setIsSubmitting(true);
    try {
      await onCreate(trimmed, 'folder', selectedColor);
      setName('');
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePreset = async (p: { name: string; color: string }) => {
    setIsSubmitting(true);
    try {
      await onCreate(p.name, 'folder', p.color);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--color-bg-secondary)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-xl)',
          maxWidth: 400,
          width: '90%',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ marginBottom: 'var(--space-lg)', color: 'var(--color-text-primary)' }}>
          {t('groups.createGroup', 'Create Group')}
        </h2>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('groups.groupNamePlaceholder', 'Work, Personal...')}
          style={{ marginBottom: 'var(--space-md)' }}
        />
        <div style={{ marginBottom: 'var(--space-md)' }}>
          <p style={{ fontSize: 'var(--font-sm)', color: 'var(--color-text-muted)', marginBottom: 8 }}>Quick select</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {PRESETS.map((p) => (
              <button
                key={p.name}
                type="button"
                onClick={() => handlePreset(p)}
                disabled={isSubmitting}
                style={{
                  padding: '8px 12px',
                  borderRadius: 'var(--radius-md)',
                  border: `2px solid ${p.color}`,
                  background: 'transparent',
                  color: 'var(--color-text-primary)',
                  cursor: 'pointer',
                }}
              >
                {p.icon} {p.name}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!name.trim() || isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
};
